import React, { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom';
import { AuthStatus, selectAuthStatus, unauthenticate } from '../../redux-ts';
import { useAppDispatch, useAppSelector } from '../../redux-ts/hooks';
import { useLoginService } from '../../services/login.service';
import { useThemeMode } from '../../providers/theme-mode.provider';
import logo from '../../assets/img/logo.png';
import styles from './header.module.css'

interface HeaderLink {
    to: string,
    text: string
}

const memberLinks: HeaderLink[] = [
    { to: '/', text: 'Buscar' },
    { to: '/collection-list', text: 'Acervo' },
    { to: '/my-repertoire', text: 'Meu repertório' },
];

const guestLinks: HeaderLink[] = [
    { to: '/be-vip', text: 'Seja VIP' },
    { to: '/contribute', text: 'Contribuir' },
];

export default function Header() {
    const [logoutInfoVisible, setLogoutInfoVisible] = useState(true);
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // Keep the member links while the session is being checked, so they don't flicker
    const isAuthenticated = useAppSelector(selectAuthStatus) !== AuthStatus.Anonymous;

    const dispatch = useAppDispatch();
    const location = useLocation();

    const service = useLoginService();
    const { mode, toggleMode } = useThemeMode();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);

        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });

        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Close the mobile menu after navigating
    useEffect(() => setMenuOpen(false), [location.pathname]);

    const onLogoutClick = async (e: React.MouseEvent) => {
        // Keep the page until the API has cleared the session cookie
        e.preventDefault();
        setLogoutInfoVisible(false);

        try {
            await service.logout();
        }
        finally {
            dispatch(unauthenticate());

            window.location.reload();
        }
    }

    const links = isAuthenticated ? memberLinks : guestLinks;

    const linkClass = ({ isActive }: { isActive: boolean }) => isActive ? `${styles.link} ${styles.active}` : styles.link;

    return <header className={`${styles.header} ${scrolled || menuOpen ? styles.scrolled : ''}`}>
        <div className={styles.inner}>
            <NavLink to='/' className={styles.brand} aria-label='Partitura BOT - início'>
                <img src={logo} alt='' className={styles.brandLogo} />
                <span className={styles.brandName}>Partitura BOT</span>
                <span className={styles.brandBadge}>VIP</span>
            </NavLink>

            <nav id='pb-main-nav' className={`${styles.nav} ${menuOpen ? styles.navOpen : ''}`}>
                <ul className={styles.links}>
                    {links.map(l => <li key={l.to}>
                        <NavLink to={l.to} end className={linkClass}>{l.text}</NavLink>
                    </li>)}
                </ul>

                <div className={styles.actions}>
                    {isAuthenticated
                        ? logoutInfoVisible && <a href='/' onClick={onLogoutClick} className={styles.ghostButton}>
                            {'Sair'}
                        </a>
                        : <NavLink to='/login' className={styles.ctaButton}>{'Entrar'}</NavLink>}
                </div>
            </nav>

            <button
                type='button'
                className={styles.themeButton}
                aria-label={mode === 'dark' ? 'Usar tema claro' : 'Usar tema escuro'}
                title={mode === 'dark' ? 'Tema claro' : 'Tema escuro'}
                onClick={toggleMode}
            >
                {mode === 'dark'
                    ? <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                        <circle cx='12' cy='12' r='4' />
                        <path d='M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41' />
                    </svg>
                    : <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                        <path d='M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z' />
                    </svg>}
            </button>

            <button
                type='button'
                className={styles.menuButton}
                aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
                aria-expanded={menuOpen}
                aria-controls='pb-main-nav'
                onClick={() => setMenuOpen(o => !o)}
            >
                <span className={`${styles.menuIcon} ${menuOpen ? styles.menuIconOpen : ''}`} />
            </button>
        </div>
    </header>
}
